<template>
    <div class="wf-field">
        <label class="wf-label">
            {{ label }} <span v-if="isRequired" class="wf-required">*</span>
        </label>
        <p v-if="instruction" class="wf-instruction">{{ instruction }}</p>

        <!-- Search bar + geolocation -->
        <div class="wf-map-search">
            <div class="wf-map-search-input-wrap">
                <i class="fal fa-search wf-map-search-icon"></i>
                <input
                    v-model="searchQuery"
                    type="text"
                    class="wf-map-search-input"
                    :placeholder="$t('wizard.location_search')"
                    @input="onSearchInput"
                    @keydown.enter.prevent="searchAddress"
                />
                <i v-if="searching" class="fal fa-spinner-third fa-spin wf-map-search-loader"></i>
                <button
                    v-else-if="searchQuery"
                    type="button"
                    class="wf-map-search-clear"
                    @click="searchQuery = ''; searchResults = []"
                >
                    <i class="fal fa-times"></i>
                </button>
            </div>
            <button
                type="button"
                class="wf-map-geolocate"
                :disabled="locating"
                @click="geolocate"
            >
                <i :class="locating ? 'fal fa-spinner-third fa-spin' : 'fal fa-location-arrow'"></i>
                <span class="wf-map-geolocate-text">{{ $t('wizard.location_geolocate') }}</span>
            </button>
        </div>

        <!-- Autocomplete results -->
        <div v-if="searchResults.length" class="wf-map-results">
            <div
                v-for="(result, i) in searchResults"
                :key="i"
                class="wf-map-result-item"
                @click="selectResult(result)"
            >
                <i class="fal fa-map-marker-alt"></i>
                <span>{{ result.display_name }}</span>
            </div>
        </div>

        <!-- Map -->
        <div class="wf-map-wrapper" :class="{ 'wf-map-wrapper--banoc': isBanoc }">
            <div class="wf-map-container" ref="mapContainer"></div>
            <div
                v-if="!isBanoc"
                class="wf-map-crosshair"
                :class="{ 'is-moving': mapMoving }"
                style="pointer-events: none;"
            >
                <i class="fal fa-crosshairs"></i>
            </div>
            <div v-if="isBanoc" class="wf-map-radius">
                <button
                    v-for="opt in radiusOptions"
                    :key="opt.value"
                    type="button"
                    class="wf-map-radius-btn"
                    :class="{ 'wf-map-radius-btn--active': radius === opt.value }"
                    @click="setRadius(opt.value)"
                >
                    {{ opt.label }}
                </button>
            </div>
        </div>

        <!-- Restriction error -->
        <div v-if="restrictionError" class="wf-map-restriction-error">
            <i class="fal fa-exclamation-triangle mr-2"></i>
            {{ restrictionError }}
        </div>

        <!-- Geocoding loader -->
        <div v-if="geocoding" class="wf-map-geocoding">
            <i class="fal fa-spinner-third fa-spin mr-2"></i>
            <span>{{ $t('forms.loading') }}...</span>
        </div>

        <!-- Selected address -->
        <div v-else-if="selectedAddress && !restrictionError" class="wf-map-address">
            <i class="fal fa-map-marker-alt mr-2"></i>
            {{ selectedAddress }}
        </div>

        <p v-if="help" class="wf-help">{{ help }}</p>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import FieldMixin from './FieldMixin'
import L from 'leaflet'
import LocationService from '@/services/LocationService'

/*
 * Bing tiles use a quadkey scheme rather than the {x}/{y}/{z} grid Leaflet
 * speaks natively, so we extend L.TileLayer to translate at request time.
 */
function bingQuadKey(x, y, z) {
    let q = ''
    for (let i = z; i > 0; i--) {
        let d = 0
        const mask = 1 << (i - 1)
        if ((x & mask) !== 0) d++
        if ((y & mask) !== 0) d += 2
        q += d
    }
    return q
}

const BingTileLayer = L.TileLayer.extend({
    getTileUrl(coords) {
        const subdomains = this.options.subdomains
        const s = subdomains[Math.abs((coords.x + coords.y) % subdomains.length)]
        return L.Util.template(this._url, {
            s,
            q: bingQuadKey(coords.x, coords.y, coords.z)
        })
    }
})

export default {
    name: 'LocationField',
    mixins: [FieldMixin],
    data() {
        return {
            map: null,
            moveTimeout: null,
            mapMoving: false,
            locationService: null,
            selectedAddress: '',
            searchQuery: '',
            searchResults: [],
            searchTimeout: null,
            locating: false,
            searching: false,
            geocoding: false,
            restrictionError: '',
            radius: (this.value && this.value.radius) || 155,
            radiusOptions: [
                { value: 180, label: '1m',   meters: 1,    zoom: 22 },
                { value: 155, label: '5m',   meters: 5,    zoom: 20 },
                { value: 130, label: '30m',  meters: 30,   zoom: 18 },
                { value: 105, label: '200m', meters: 200,  zoom: 16 },
                { value: 80,  label: '1km',  meters: 1000, zoom: 14 }
            ],
            radiusShape: null
        }
    },
    computed: {
        mapConfig() {
            if (!this.attribute.config || !this.attribute.config.map) return null
            return this.attribute.config.map.config || {}
        },
        center() {
            if (!this.mapConfig || !this.mapConfig.center) return [48.8566, 2.3522]
            const c = this.mapConfig.center
            if (Array.isArray(c)) return c
            return [c.lat || 48.8566, c.lng || c.lon || 2.3522]
        },
        zoom() {
            return (this.mapConfig && this.mapConfig.zoom) || 13
        },
        provider() {
            return (this.mapConfig && this.mapConfig.type) || 'osm'
        },
        isBanoc() {
            return this.provider === 'banoc'
        },
        radiusMeters() {
            const opt = this.radiusOptions.find((o) => o.value === this.radius)
            return opt ? opt.meters : 30
        },
        radiusMaxZoom() {
            const opt = this.radiusOptions.find((o) => o.value === this.radius)
            return opt ? opt.zoom : 18
        },
        isSatellite() {
            return this.mapConfig && this.mapConfig.carte === 'satellite'
        },
        restrictionCountry() {
            if (!this.mapConfig || !this.mapConfig.restriction) return null
            return this.mapConfig.restriction.country || null
        },
        restrictionCity() {
            if (!this.mapConfig || !this.mapConfig.restriction) return null
            return this.mapConfig.restriction.city || null
        }
    },
    mounted() {
        this.initGeocoder()
        this.$nextTick(() => {
            this.initMap()
        })
    },
    beforeDestroy() {
        if (this.map) this.map.remove()
        clearTimeout(this.searchTimeout)
        clearTimeout(this.moveTimeout)
    },
    methods: {
        createTileLayer() {
            const baseOpts = { maxZoom: 22, maxNativeZoom: 19 }
            const bingSubdomains = ['0', '1', '2', '3']

            if (this.isSatellite) {
                if (this.provider === 'bing') {
                    return new BingTileLayer(
                        'https://t{s}.ssl.ak.tiles.virtualearth.net/tiles/a{q}.jpeg?g=129',
                        {
                            ...baseOpts,
                            subdomains: bingSubdomains,
                            attribution: '&copy; Microsoft Bing Maps'
                        }
                    )
                }
                return L.tileLayer(
                    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
                    { ...baseOpts, attribution: '&copy; Esri WorldImagery' }
                )
            }

            if (this.provider === 'bing') {
                return new BingTileLayer(
                    'https://t{s}.ssl.ak.tiles.virtualearth.net/tiles/r{q}.jpeg?g=129&mkt=fr-FR',
                    {
                        ...baseOpts,
                        subdomains: bingSubdomains,
                        attribution: '&copy; Microsoft Bing Maps'
                    }
                )
            }

            if (this.provider === 'maps') {
                return L.tileLayer(
                    'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
                    { ...baseOpts, attribution: '&copy; Google Maps' }
                )
            }

            if (this.provider === 'banoc') {
                return L.tileLayer(
                    'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                    { ...baseOpts, attribution: '&copy; BANOC' }
                )
            }

            return L.tileLayer(
                'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                { ...baseOpts, attribution: '&copy; OpenStreetMap contributors' }
            )
        },
        initMap() {
            if (!this.$refs.mapContainer) return

            // Restore existing value or use default center
            const initialCenter = (this.value && this.value.lat)
                ? [this.value.lat, this.value.lon || this.value.lng]
                : this.center
            const initialZoom = (this.value && this.value.lat)
                ? Math.max(this.zoom, 15)
                : this.zoom

            this.map = L.map(this.$refs.mapContainer).setView(initialCenter, initialZoom)

            this.createTileLayer().addTo(this.map)

            if (this.isBanoc) {
                const initialLatLng = L.latLng(initialCenter[0], initialCenter[1])
                this.radiusShape = L.rectangle(initialLatLng.toBounds(this.radiusMeters * 2), {
                    color: '#0891b2',
                    fillColor: '#0891b2',
                    fillOpacity: 0.15,
                    weight: 2,
                    interactive: false
                }).addTo(this.map)

                this.map.on('click', this.onBanocClick)
                this.map.fitBounds(this.radiusShape.getBounds(), { padding: [40, 40], maxZoom: this.radiusMaxZoom })
                this.reverseGeocode(initialLatLng)
            } else {
                this.map.on('movestart', () => {
                    this.mapMoving = true
                })

                this.map.on('moveend', () => {
                    this.mapMoving = false
                    clearTimeout(this.moveTimeout)
                    this.moveTimeout = setTimeout(() => {
                        this.reverseGeocode(this.map.getCenter())
                    }, 500)
                })
            }

            // Restore existing value
            if (this.value && this.value.lat) {
                const latlng = L.latLng(this.value.lat, this.value.lon || this.value.lng)
                this.map.setView(latlng, Math.max(this.zoom, 15))
                this.selectedAddress = this.value.address || ''
                this.searchQuery = this.value.address || ''
            } else if (!this.isBanoc) {
                // Geocode initial center so the address is shown on first render
                this.$nextTick(() => this.reverseGeocode(this.map.getCenter()))
            }

            setTimeout(() => this.map.invalidateSize(), 200)
        },

        panTo(latlng) {
            this.map.setView(latlng, Math.max(this.map.getZoom(), 15))
        },

        emitLocation(lat, lon, address) {
            this.selectedAddress = address
            this.searchQuery = address
            this.searchResults = []
            const payload = { lat, lon, address }
            if (this.isBanoc) payload.radius = this.radius
            this.emitValue(payload)
        },

        setRadius(value) {
            this.radius = value
            if (this.radiusShape && this.map) {
                const center = this.isBanoc
                    ? this.radiusShape.getBounds().getCenter()
                    : this.map.getCenter()
                const bounds = center.toBounds(this.radiusMeters * 2)
                this.radiusShape.setBounds(bounds)
                this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: this.radiusMaxZoom })
                if (this.isBanoc) {
                    this.reverseGeocode(center)
                }
            }
            if (this.value && this.value.lat) {
                this.emitValue({ ...this.value, radius: value })
            }
        },

        onBanocClick(e) {
            if (this.radiusShape) {
                const bounds = e.latlng.toBounds(this.radiusMeters * 2)
                this.radiusShape.setBounds(bounds)
                this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: this.radiusMaxZoom })
            }
            this.reverseGeocode(e.latlng)
        },

        // -- Init geocoder using LocationService (same as old wizard) --
        initGeocoder() {
            const companyId = this.$store.state.auth && this.$store.state.auth.company
                ? this.$store.state.auth.company.id
                : null

            const config = {
                mapProvider: this.provider,
                restriction: {
                    country: this.restrictionCountry || '',
                    city: this.restrictionCity || ''
                }
            }

            try {
                this.locationService = new LocationService(config, companyId)
            } catch (e) {
                console.warn('LocationService init failed, provider:', this.provider, e)
            }
        },

        // -- Search --
        onSearchInput() {
            clearTimeout(this.searchTimeout)
            if (this.searchQuery.length < 3) {
                this.searchResults = []
                return
            }
            this.searchTimeout = setTimeout(() => this.searchAddress(), 400)
        },

        async searchAddress() {
            if (this.searchQuery.length < 3) return
            this.searching = true
            try {
                let results = []

                if (this.locationService) {
                    // Configured provider drives the request — Bing / Google /
                    // OSM. Don't second-guess with a Nominatim follow-up: empty
                    // means "no match" for the user's chosen provider.
                    try {
                        results = (await this.locationService.search(this.searchQuery)) || []
                    } catch (e) {
                        results = []
                    }
                } else {
                    // No provider configured at all — last-resort Nominatim.
                    const params = new URLSearchParams({
                        q: this.searchQuery,
                        format: 'json',
                        limit: '5',
                        addressdetails: '1',
                        'accept-language': 'fr'
                    })
                    if (this.restrictionCountry) {
                        params.set('countrycodes', this.restrictionCountry.toLowerCase().slice(0, 2))
                    }
                    const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`)
                    const data = await res.json()
                    results = data.map((r) => ({
                        address: r.display_name,
                        lat: r.lat,
                        lon: r.lon
                    }))
                }

                this.searchResults = (results || []).map((r) => ({
                    display_name: r.address,
                    lat: r.lat,
                    lon: r.lon
                }))
            } catch (e) {
                this.searchResults = []
            } finally {
                this.searching = false
            }
        },

        selectResult(result) {
            const latlng = L.latLng(parseFloat(result.lat), parseFloat(result.lon))
            this.searchQuery = result.display_name
            this.searchResults = []
            this.restrictionError = ''
            this.panTo(latlng)
            this.checkRestrictionAndEmit(latlng, result.display_name)
        },

        // -- Reverse geocode with restriction check --
        async reverseGeocode(latlng) {
            this.restrictionError = ''
            this.geocoding = true
            try {
                let address = ''

                if (this.locationService) {
                    try {
                        const params = { lat: latlng.lat, lon: latlng.lng }
                        if (this.isBanoc) params.level = this.radius
                        address = (await this.locationService.reverseSearch(params)) || ''
                    } catch (e) {
                        address = ''
                    }
                } else {
                    // No provider configured — direct Nominatim as last resort.
                    const res = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}&addressdetails=1&accept-language=fr`
                    )
                    const data = await res.json()
                    address = data.display_name || ''
                }

                if (!address) {
                    address = `${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`
                }

                this.checkRestrictionAndEmit(latlng, address)
            } catch (e) {
                const address = `${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`
                this.emitLocation(latlng.lat, latlng.lng, address)
            } finally {
                this.geocoding = false
            }
        },

        // -- Check restriction then emit --
        async checkRestrictionAndEmit(latlng, address) {
            if (!this.restrictionCountry && !this.restrictionCity) {
                this.emitLocation(latlng.lat, latlng.lng, address)
                return
            }

            // Need Nominatim addressdetails for restriction check
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}&addressdetails=1&accept-language=fr`
                )
                const data = await res.json()
                const addr = data.address || {}

                if (this.restrictionCountry) {
                    const normalize = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
                    const restriction = normalize(this.restrictionCountry.trim())
                    const countryCode = (addr.country_code || '').toLowerCase()
                    const countryName = normalize(addr.country || '')
                    // Match ISO code ("cm"), first-2-letters shorthand (backward compat),
                    // or full country name in whatever language Nominatim returns.
                    const match = countryCode === restriction
                        || countryCode === restriction.slice(0, 2)
                        || countryName === restriction
                        || countryName.includes(restriction)
                        || restriction.includes(countryName)
                    if (!match) {
                        this.restrictionError = this.$t('wizard.location_restriction_country', { country: this.restrictionCountry })
                        this.removeMarker()
                        this.emitValue(null)
                        return
                    }
                }

                if (this.restrictionCity) {
                    const restriction = this.restrictionCity.toLowerCase().trim()
                    // Check all possible location fields from Nominatim
                    const candidates = [
                        addr.city, addr.town, addr.village, addr.municipality,
                        addr.suburb, addr.city_district, addr.county,
                        addr.state, addr.hamlet
                    ].filter(Boolean).map((v) => v.toLowerCase())

                    const match = candidates.some((c) =>
                        c === restriction || c.includes(restriction) || restriction.includes(c)
                    )
                    if (!match) {
                        this.restrictionError = this.$t('wizard.location_restriction_city', { city: this.restrictionCity })
                        this.removeMarker()
                        this.emitValue(null)
                        return
                    }
                }

                this.restrictionError = ''
                this.emitLocation(latlng.lat, latlng.lng, address)
            } catch (e) {
                this.emitLocation(latlng.lat, latlng.lng, address)
            }
        },

        removeMarker() {
            this.selectedAddress = ''
        },

        // -- Geolocation --
        geolocate() {
            if (!navigator.geolocation) return
            this.locating = true
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const latlng = L.latLng(pos.coords.latitude, pos.coords.longitude)
                    this.panTo(latlng)
                    this.locating = false
                },
                (err) => {
                    console.error('Geolocation error:', err)
                    this.locating = false
                },
                { enableHighAccuracy: true, timeout: 10000 }
            )
        }
    }
}
</script>
